import React from 'react';
import { Hash, Users, CheckCircle, XCircle, Mail, UserPlus } from 'lucide-react';

interface GroupBadgeProps {
  name: string;
  color?: string;
  isSystem?: boolean;
  count?: number;
  active?: boolean;
  onClick?: () => void;
  onRemove?: () => void;
  size?: 'sm' | 'md';
}

const SYSTEM_ICONS: Record<string, React.ReactNode> = {
  'all': <Users className="h-3 w-3" />,
  'rsvp-accepted': <CheckCircle className="h-3 w-3" />,
  'rsvp-declined': <XCircle className="h-3 w-3" />,
  'rsvp-invited': <Mail className="h-3 w-3" />,
  'rsvp-new': <UserPlus className="h-3 w-3" />,
};

const COLOR_MAP: Record<string, string> = {
  blue: 'bg-blue-100 text-blue-800 border-blue-200 hover:bg-blue-200',
  green: 'bg-green-100 text-green-800 border-green-200 hover:bg-green-200',
  red: 'bg-red-100 text-red-800 border-red-200 hover:bg-red-200',
  yellow: 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-200',
  gray: 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200',
  purple: 'bg-purple-100 text-purple-800 border-purple-200 hover:bg-purple-200',
  pink: 'bg-pink-100 text-pink-800 border-pink-200 hover:bg-pink-200',
  orange: 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200',
  teal: 'bg-teal-100 text-teal-800 border-teal-200 hover:bg-teal-200',
};

const ACTIVE_COLOR_MAP: Record<string, string> = {
  blue: 'bg-blue-500 text-white border-blue-600',
  green: 'bg-green-500 text-white border-green-600',
  red: 'bg-red-500 text-white border-red-600',
  yellow: 'bg-yellow-500 text-white border-yellow-600',
  gray: 'bg-gray-500 text-white border-gray-600',
  purple: 'bg-purple-500 text-white border-purple-600',
  pink: 'bg-pink-500 text-white border-pink-600',
  orange: 'bg-orange-500 text-white border-orange-600',
  teal: 'bg-teal-500 text-white border-teal-600',
};

export const GroupBadge: React.FC<GroupBadgeProps> = ({
  name,
  color = 'blue',
  isSystem = false,
  count,
  active = false,
  onClick,
  onRemove,
  size = 'sm',
}) => {
  const colorClass = active
    ? (ACTIVE_COLOR_MAP[color] || ACTIVE_COLOR_MAP.blue)
    : (COLOR_MAP[color] || COLOR_MAP.blue);

  const sizeClass = size === 'sm' 
    ? 'px-2 py-0.5 text-xs' 
    : 'px-3 py-1 text-sm';

  const icon = isSystem ? SYSTEM_ICONS[name] : <Hash className="h-3 w-3" />;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border font-medium transition-colors ${colorClass} ${sizeClass} ${onClick ? 'cursor-pointer' : 'cursor-default'}`}
    >
      {icon}
      <span>{name}</span>
      {count !== undefined && (
        <span className={`ml-0.5 ${active ? 'opacity-80' : 'opacity-60'}`}>({count})</span>
      )}
      {onRemove && !isSystem && (
        <span
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="ml-0.5 hover:opacity-100 opacity-60 cursor-pointer"
          role="button"
          tabIndex={0}
        >
          ×
        </span>
      )}
    </button>
  );
};
